import React, { useState, useEffect } from 'react';
import { Loader, AlertCircle } from 'lucide-react';
import { getStoreInfo, getMediaUrl } from '../services/api';

const BookingPage = () => {
  const [storeInfo, setStoreInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // 获取店铺信息
  useEffect(() => {
    const fetchStoreInfo = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const data = await getStoreInfo();
        console.log('📊 获取到的店铺信息:', data);

        const hasStoreInfo = data && (
          data.name || data.address || data.businessHours || data.contactNumber ||
          data.wechatAccount || data.wechatQRCode || data.description ||
          (Array.isArray(data.images) && data.images.length > 0)
        );

        if (hasStoreInfo) {
          setStoreInfo({
            name: data.name || '',
            address: data.address || '',
            zipCode: data.zipCode || '',
            businessHours: data.businessHours || '',
            contactNumber: data.contactNumber,
            wechatAccount: data.wechatAccount,
            wechatQRCode: getMediaUrl(data.wechatQRCode),
            description: data.description || '',
            images: (data.images || []).map(getMediaUrl)
          });
        } else {
          setStoreInfo(null);
        }
      } catch (err) {
        console.error('❌ 获取店铺信息失败:', err);
        setError(err.message);
        setStoreInfo(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStoreInfo();
  }, []);

  // Loading状态
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 text-sm">正在加载店铺信息...</p>
        </div>
      </div>
    );
  }

  // Error状态
  if (error && !storeInfo) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-800 font-medium mb-2">加载失败</p>
          <p className="text-gray-600 text-sm mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            重新加载
          </button>
        </div>
      </div>
    );
  }

  if (!storeInfo) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <img src="/images/askfirst-logo.png" alt="先问 ASKFIRST" className="h-20 w-36 object-contain rounded-lg mx-auto mb-5 invert" />
          <p className="text-gray-800 font-medium mb-2">店铺信息尚未配置</p>
          <p className="text-gray-500 text-sm">请先在 CMS 中完善店铺名称、地址和营业时间</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Banner Area - 极简大留白 (45% of viewport) */}
      <div className="relative w-full" style={{ height: 'calc(100vh * 0.45)' }}>
        <div className="w-full h-full bg-white flex flex-col items-center justify-center">
          <h1 className="text-3xl font-bold tracking-[0.3em] text-gray-900 mb-6">
            ASKFIRST
          </h1>
          <img
            src="/images/askfirst-logo.png"
            alt="先问 ASKFIRST"
            className="h-20 w-36 object-contain rounded-lg invert"
          />
        </div>
      </div>

      {/* 页面内容区域 */}
      <div className="px-4 py-4">
        {/* 服务项目 */}
        <div className="space-y-2 mb-6 bg-gray-50 rounded-lg p-3">
          {/* 十年一刻电影 */}
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center p-1.5">
                <img
                  src="/images/askfirst-logo.png"
                  alt="先问 ASKFIRST"
                  className="w-full h-full object-contain rounded invert"
                />
              </div>
              <div>
                <h3 className="text-lg font-medium">影像定制与咨询</h3>
              </div>
            </div>
            <button
              type="button"
              disabled={!storeInfo.wechatAccount && !storeInfo.wechatQRCode}
              onClick={() => document.getElementById('wechat-contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="py-1.5 px-4 bg-white border-2 border-gray-300 rounded-full text-gray-700 text-sm font-medium flex items-center gap-2 disabled:opacity-40"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.27-.027-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z"/>
              </svg>
              即刻关注
            </button>
          </div>

          {/* 客服咨询 */}
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium">客服咨询</h3>
              </div>
            </div>
            <a
              href={storeInfo.contactNumber ? `tel:${storeInfo.contactNumber}` : undefined}
              aria-disabled={!storeInfo.contactNumber}
              className="py-1.5 px-4 bg-white border-2 border-gray-300 rounded-full text-gray-700 text-sm font-medium flex items-center gap-2 aria-disabled:opacity-40"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              即刻咨询
            </a>
          </div>
        </div>

        {/* 店铺信息与资讯卡片容器 */}
        <div className="">
          {/* 店铺信息 */}
          <div className="text-left mb-3">
            <h1 className="text-lg font-bold">{storeInfo.name}</h1>
          </div>

          {/* 营业地点实景图片 */}
          {storeInfo.images.length > 0 && (
            <div className="grid grid-cols-2 gap-2 mb-4">
              {storeInfo.images.map((image, index) => (
                <div key={image} className={`rounded-lg overflow-hidden ${index === 0 ? 'col-span-2 h-40' : 'h-28'}`}>
                  <img
                    src={image}
                    alt={`${storeInfo.name}实景 ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          )}

          {storeInfo.description && (
            <p className="text-sm text-gray-600 leading-6 mb-4">{storeInfo.description}</p>
          )}

          {/* 地址和营业时间信息 */}
          <div className="text-left space-y-3">
            {/* 地址信息 */}
            {storeInfo.address && <div>
              <p className="text-gray-400 text-xs mb-1">地址</p>
              <p className="text-gray-700 text-sm">
                {storeInfo.address}{storeInfo.zipCode ? ` · ${storeInfo.zipCode}` : ''}
              </p>
            </div>}

            {/* 营业时间信息 */}
            {storeInfo.businessHours && <div>
              <p className="text-gray-400 text-xs mb-1">营业时间</p>
              <p className="text-gray-700 text-sm">{storeInfo.businessHours}</p>
            </div>}

            {/* 联系电话（如果有） */}
            {storeInfo.contactNumber && (
              <div>
                <p className="text-gray-400 text-xs mb-1">联系电话</p>
                <a href={`tel:${storeInfo.contactNumber}`} className="text-gray-700 text-sm underline underline-offset-2">
                  {storeInfo.contactNumber}
                </a>
              </div>
            )}

            {storeInfo.wechatAccount && (
              <div>
                <p className="text-gray-400 text-xs mb-1">微信账号</p>
                <p className="text-gray-700 text-sm select-all">{storeInfo.wechatAccount}</p>
              </div>
            )}
          </div>
        </div>

        {(storeInfo.wechatQRCode || storeInfo.wechatAccount) && (
          <div id="wechat-contact" className="mt-6 rounded-xl bg-gray-50 p-5 text-center">
            <h3 className="font-medium text-gray-900">微信咨询</h3>
            {storeInfo.wechatQRCode && (
              <img
                src={storeInfo.wechatQRCode}
                alt="客服微信二维码"
                className="w-40 h-40 object-contain mx-auto mt-4 bg-white rounded-lg"
              />
            )}
            {storeInfo.wechatAccount && (
              <p className="mt-3 text-sm text-gray-600">微信号：{storeInfo.wechatAccount}</p>
            )}
          </div>
        )}

        {/* 预约提示 */}
        <div className="mt-8 mb-20">
          <div className="bg-gray-50 rounded-xl p-6 text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">预约拍摄服务</h3>
            <p className="text-sm text-gray-600">
              如需预约拍摄服务，请联系客服获取预约表单链接
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
